import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [result, setResult] = useState(null);
  const [failed, setFailed] = useState(false);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setFailed(true);
      return;
    }
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setResult(data);
          return;
        }
      } catch (e) {}
      attempts.current += 1;
      if (attempts.current < 20) setTimeout(poll, 2000);
      else setFailed(true);
    };
    poll();
  }, [sessionId]);

  return (
    <main className="min-h-screen flex items-center justify-center px-4 pt-16" data-testid="payment-success-page">
      <Reveal className="gold-border-card p-10 max-w-lg text-center w-full">
        {!result && !failed && (
          <>
            <Loader2 className="text-amber-400 mx-auto animate-spin" size={40} />
            <h1 className="font-display font-bold uppercase text-xl tracking-tight mt-6">Confirming your payment…</h1>
            <p className="text-zinc-500 text-sm mt-3">This takes a few seconds.</p>
          </>
        )}
        {result && (
          <>
            <CheckCircle2 className="text-amber-400 mx-auto" size={44} />
            <h1 className="font-display font-bold uppercase text-2xl tracking-tight mt-6" data-testid="payment-confirmed-heading">
              {result.type === "gift_card" ? "Gift Card Delivered" : "Deposit Confirmed"}
            </h1>
            {result.type === "gift_card" ? (
              <div className="mt-5">
                <p className="text-zinc-400 text-sm">Your ${result.amount} voucher has been emailed to {result.recipient_email}.</p>
                {result.gift_code && (
                  <p className="font-mono-gold text-amber-300 tracking-[0.3em] text-lg mt-5 border border-amber-500/30 rounded-lg py-3" data-testid="payment-gift-code">
                    {result.gift_code}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-zinc-400 text-sm mt-5">Your booking is locked in and a confirmation email is on its way. The remaining balance is due on the day.</p>
            )}
            <Link to="/account" data-testid="payment-view-account-link" className="inline-block mt-8 gold-foil text-black font-semibold uppercase tracking-[0.2em] text-xs px-8 py-3.5 rounded-full">
              View My Account
            </Link>
          </>
        )}
        {failed && (
          <>
            <XCircle className="text-zinc-500 mx-auto" size={44} />
            <h1 className="font-display font-bold uppercase text-xl tracking-tight mt-6">Still processing</h1>
            <p className="text-zinc-500 text-sm mt-3">We could not confirm the payment yet. Check your account in a moment, or contact the concierge line.</p>
            <Link to="/account" data-testid="payment-pending-account-link" className="inline-block mt-8 border border-amber-500/40 text-amber-200 font-semibold uppercase tracking-[0.2em] text-xs px-8 py-3.5 rounded-full">
              My Account
            </Link>
          </>
        )}
      </Reveal>
    </main>
  );
}

export function PaymentCancel() {
  return (
    <main className="min-h-screen flex items-center justify-center px-4 pt-16" data-testid="payment-cancel-page">
      <Reveal className="gold-border-card p-10 max-w-md text-center">
        <XCircle className="text-zinc-500 mx-auto" size={44} />
        <h1 className="font-display font-bold uppercase text-xl tracking-tight mt-6">Checkout cancelled</h1>
        <p className="text-zinc-500 text-sm mt-3">No charge was made. You can pick up where you left off anytime.</p>
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/book" data-testid="cancel-back-to-booking-link" className="gold-foil text-black font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 rounded-full">
            Back to Booking
          </Link>
          <Link to="/gift-cards" data-testid="cancel-back-to-gifts-link" className="border border-amber-500/40 text-amber-200 font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 rounded-full">
            Gift Cards
          </Link>
        </div>
      </Reveal>
    </main>
  );
}
